/**
 * Figma Node IDs:
 * Design Frame: 385:19706
 * Instance IDs: I1497:75191, I1497:76407, I1497:76682, I1497:77380, I385:19709, I1497:75904
 * Composite: true
 * Last Updated: 2025-01-15
 * Generated by: Claude Code figma-component command
 */
import * as React from "react";
import { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useIntl, defineMessages } from "react-intl";
import FocusLock from "react-focus-lock";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";

import { CircleX } from "@labxchange/icons";
import { isUsersnapActive } from "@labxchange/util-usersnap";

import { LxButton } from "../LxButton";
import { fetchTranslations } from "./locales/config";
import * as styles from "./Modal.css";

const messages = defineMessages({
  close: {
    id: "modal.close",
    defaultMessage: "Close",
    description: "Close button text for modal",
  },
});

export type ModalSize = "small" | "medium" | "large";

export interface ModalProps {
  /** Content to render inside the modal */
  children: React.ReactNode;

  /** Controls whether the modal is open (controlled mode). If not provided, modal manages its own state. */
  isOpen?: boolean;
  /** Optional callback fired before the modal closes */
  onClose?: () => void;
  /** Additional CSS class for the modal container */
  className?: string;
  /** Test identifier for automated testing */
  testId?: string;
  /** Accessible label for the modal dialog */
  ariaLabel?: string;
  /** ID of element describing the modal content */
  ariaDescribedBy?: string;

  /** Custom translation message ID for close button text (default: "modal.close") */
  closeButtonText?: string;
  /** Whether to show the close button (default: true) */
  showCloseButton?: boolean;
  /** Size variant controlling max-width (default: "medium") */
  size?: ModalSize;
  /** Render modal inline without portal (for documentation/preview purposes) */
  inline?: boolean;
}

const ModalInternal: React.FC<ModalProps> = ({
  children,
  isOpen: isOpenProp,
  onClose,
  className,
  testId,
  ariaLabel,
  ariaDescribedBy,
  showCloseButton = true,
  size = "medium",
  inline = false,
}) => {
  const intl = useIntl();
  const isControlled = isOpenProp !== undefined;
  const [internalIsOpen, setInternalIsOpen] = useState(true);
  const isOpen = isControlled ? isOpenProp : internalIsOpen;

  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    onClose?.();
    if (!isControlled) {
      setInternalIsOpen(false);
    }
  }, [onClose, isControlled]);

  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleClose]);

  // Close on outside click
  useEffect(() => {
    if (inline || !isOpen) return;

    const handlePointerDown = (e: PointerEvent) => {
      const dialogEl = modalRef.current;
      const overlayEl = overlayRef.current;
      if (!dialogEl || !overlayEl) return;

      const target = e.target as Node | null;
      if (!target) return;

      // Only treat it as "outside" if the event happened within the overlay portal subtree,
      // and not within the dialog itself.
      if (overlayEl.contains(target) && !dialogEl.contains(target)) {
        handleClose();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [handleClose, inline, isOpen]);

  // Focus modal container on mount for accessibility
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  if (!isOpen) return null;

  const combinedClassName = [
    styles.modalContainer,
    styles.sizeVariants[size],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const modalDialog = (
    <div
      ref={modalRef}
      className={combinedClassName}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      data-testid={testId}
      tabIndex={-1}
    >
      {showCloseButton && (
        <div className={styles.closeButtonContainer}>
          <LxButton
            variant="oncolor"
            size="xs"
            text={intl.formatMessage(messages.close)}
            Icon={CircleX}
            iconPosition="end"
            onClickHandler={handleClose}
            testId="modal-close-btn"
          />
        </div>
      )}
      <div className={styles.contentContainer}>{children}</div>
    </div>
  );

  if (inline) {
    return (
      <FocusLock disabled={isUsersnapActive()}>
        <div
          className={styles.inlineContainer}
          data-testid={testId ? `${testId}-container` : undefined}
        >
          {modalDialog}
        </div>
      </FocusLock>
    );
  }

  const modalContent = (
    <FocusLock disabled={isUsersnapActive()}>
      <div
        ref={overlayRef}
        className={styles.overlay}
        data-testid={testId ? `${testId}-overlay` : undefined}
      >
        {modalDialog}
      </div>
    </FocusLock>
  );

  return createPortal(modalContent, document.body);
};

export const Modal: React.FC<ModalProps> = (props) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <ModalInternal {...props} />
  </TranslationsProvider>
);

export default Modal;
